
import { OnboardingGuard } from '@components/auth/OnboardingGuard';
import { ProtectedRoute } from '@components/auth/ProtectedRoute';
import { AdminLayout } from '@components/layout/AdminLayout';
import { AppLayout } from '@components/layout/AppLayout';
import { AuthLayout } from '@components/layout/AuthLayout';
import { useAuthStore } from '@stores/auth.store';
import { ToastContainer } from '@ui/index';
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

// --- Auth pages -----------------------------------------------------------
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@pages/auth/VerifyEmailPage'));

// --- Onboarding ------------------------------------------------------------
const OnboardingPage = lazy(() => import('@pages/onboarding/OnboardingPage'));
const ConnectWhatsAppPage = lazy(
  () => import('@pages/onboarding/ConnectWhatsAppPage'),
);

// --- App pages -------------------------------------------------------------
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'));
const InboxPage = lazy(() => import('@pages/inbox/InboxPage'));
const ContactsPage = lazy(() => import('@pages/contacts/ContactsPage'));
const ContactDetailPage = lazy(() => import('@pages/contacts/ContactDetailPage'));
const CampaignsPage = lazy(() => import('@pages/campaigns/CampaignsPage'));
const CampaignWizardPage = lazy(() => import('@pages/campaigns/CampaignWizardPage'));
const CampaignDetailPage = lazy(() => import('@pages/campaigns/CampaignDetailPage'));
const TemplatesPage = lazy(() => import('@pages/templates/TemplatesPage'));
const TemplateBuilderPage = lazy(() => import('@pages/templates/TemplateBuilderPage'));
// Automation pages are kept in the source tree but no longer
// referenced from any route (see docs/deferred-features.md). The
// lazy imports are removed so Vite drops them from the production
// bundle entirely.
const ProjectsListPage = lazy(() => import('@pages/projects/ProjectsListPage'));
const ProjectDetailsPage = lazy(() => import('@pages/projects/ProjectDetailsPage'));
const AnalyticsPage = lazy(() => import('@pages/analytics/AnalyticsPage'));
const BillingPage = lazy(() => import('@pages/billing/BillingPage'));
const SettingsPage = lazy(() => import('@pages/settings/SettingsPage'));
const ProfilePage = lazy(() => import('@pages/profile/ProfilePage'));

// --- Super admin pages -----------------------------------------------------
const AdminOverviewPage = lazy(() => import('@pages/super-admin/AdminOverviewPage'));
const AdminBusinessesPage = lazy(() => import('@pages/super-admin/AdminBusinessesPage'));
const AdminBusinessDetailPage = lazy(
  () => import('@pages/super-admin/AdminBusinessDetailPage'),
);
const AdminProjectPricingPage = lazy(
  () => import('@pages/super-admin/AdminProjectPricingPage'),
);
const AdminAnalyticsPage = lazy(() => import('@pages/super-admin/AdminAnalyticsPage'));
const AdminReconciliationPage = lazy(
  () => import('@pages/super-admin/AdminReconciliationPage'),
);
const AdminSettingsPage = lazy(() => import('@pages/super-admin/AdminSettingsPage'));
const AdminAtRiskPage = lazy(() => import('@pages/super-admin/AdminAtRiskPage'));

// --- Misc ------------------------------------------------------------------
const NotFoundPage = lazy(() => import('@pages/NotFoundPage'));
const ComponentShowcase = lazy(() => import('@pages/dev/ComponentShowcase'));
// Development only — the import is dropped from production builds.
const CampaignDetailPreview = import.meta.env.DEV
  ? lazy(() => import('@pages/dev/CampaignDetailPreview'))
  : null;

function LoadingShell(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-apple-canvas">
      <div className="animate-pulse text-[13px] text-apple-gray-500">Loading…</div>
    </div>
  );
}

function Guarded({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ProtectedRoute>
      <OnboardingGuard>{children}</OnboardingGuard>
    </ProtectedRoute>
  );
}

/**
 * Index route element. Super-admins land on /admin (their primary
 * workspace); everyone else gets the customer dashboard. Covers the
 * direct-load case (bookmark, refresh) where the LoginPage's post-
 * login redirect has already happened in a prior session.
 */
function IndexLanding(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  if (role === 'super_admin') return <Navigate to="/admin" replace />;
  return <DashboardPage />;
}

export default function App(): JSX.Element {
  return (
    <>
      <Suspense fallback={<LoadingShell />}>
        <Routes>
          {/* Public — auth flow */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
          </Route>

          {/* Onboarding — authenticated but not complete */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/connect-whatsapp"
            element={
              <ProtectedRoute>
                <ConnectWhatsAppPage />
              </ProtectedRoute>
            }
          />

          {/* Main app — authenticated + onboarded */}
          <Route
            element={
              <Guarded>
                <AppLayout />
              </Guarded>
            }
          >
            <Route index element={<IndexLanding />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/:conversationId" element={<InboxPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/contacts/:contactId" element={<ContactDetailPage />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/new" element={<CampaignWizardPage />} />
            <Route path="/campaigns/:id/edit" element={<CampaignWizardPage />} />
            <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/templates/new" element={<TemplateBuilderPage />} />
            <Route path="/templates/:id/edit" element={<TemplateBuilderPage />} />
            {/* Automation routes are intentionally redirected to the
                dashboard until the audit findings (see
                docs/deferred-features.md) are resolved. The page
                components remain in the bundle so re-enabling is a
                one-line swap. */}
            <Route path="/automation" element={<Navigate to="/" replace />} />
            <Route path="/automation/new" element={<Navigate to="/" replace />} />
            <Route path="/automation/:id/edit" element={<Navigate to="/" replace />} />
            {/* Catalog hidden for now (feature not built) — redirect to home. */}
            <Route path="/catalog" element={<Navigate to="/" replace />} />
            <Route path="/projects" element={<ProjectsListPage />} />
            <Route path="/projects/:id" element={<ProjectDetailsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/:tab" element={<SettingsPage />} />
            <Route path="/profile" element={<ProfilePage />} />

          </Route>

          {/* Super admin — own top-level shell, completely separate
              from the customer AppLayout. Own sidebar, own topbar, no
              project switcher. Phase-2 "full admin layout" promised
              and finally delivered (see SuperAdminLayout history). */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['super_admin']} roleFallback="/login">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminOverviewPage />} />
            <Route path="businesses" element={<AdminBusinessesPage />} />
            <Route path="businesses/:orgId" element={<AdminBusinessDetailPage />} />
            <Route
              path="projects/:projectId/pricing"
              element={<AdminProjectPricingPage />}
            />
            <Route path="at-risk" element={<AdminAtRiskPage />} />
            <Route path="plans" element={<AdminSettingsPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="reconciliation" element={<AdminReconciliationPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>

          {/* Dev-only route — remains until Phase 2 */}
          <Route path="/dev/components" element={<ComponentShowcase />} />
          {/* Sample-data preview of the campaign page; development builds only. */}
          {CampaignDetailPreview ? (
            <Route path="/dev/campaign-preview/:id" element={<CampaignDetailPreview />} />
          ) : null}

          {/* Legacy redirect from earlier placeholder */}
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <ToastContainer />
    </>
  );
}
